import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AlertController, IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionDropdownComponent } from 'src/app/components/action-dropdown/action-dropdown.component';
import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import { EditTagLinksComponent } from 'src/app/components/edit-tag-links/edit-tag-links.component';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { INTEGRATIONS, PRIVATE_CHANNEL_NAME, SUPPORT_STEP_ASSIGNEE_TYPES } from 'src/app/constants';
import { SelectedFilterMap } from 'src/app/utils/filter-utils';
import { downloadCSV, getWhereClausesFromFilters } from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { WhereClause } from 'src/models';
import { Account } from 'src/models/accounts';
import { PROJECT_VISIBILITY } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AccountsService } from 'src/services/accounts.service';
import { AnalyticsService } from 'src/services/analytics.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';
import { UserGroupsService } from 'src/services/user-groups.service';
import { UsersService } from 'src/services/users.service';

import { EditAccountComponent } from '../edit-account/edit-account.component';

@Component({
  standalone: true,
  imports: [
    SharedDirectivesModule,
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    AvatarModule,
    SharedPipesModule,
    NgxTippyModule,
    FilterButtonComponent,
    RouterModule,
  ],
  selector: 'app-accounts-list',
  templateUrl: './accounts-list.page.html',
  styleUrls: ['./accounts-list.page.css'],
})
export class AccountsListPage implements OnInit {
  PRIVATE_CHANNEL_NAME = PRIVATE_CHANNEL_NAME;

  filters: SelectedFilterMap = new Map();

  allAccounts: Partial<Account>[];
  selectedAccounts: Partial<Account>[] = [];

  public readonly accounts$: BehaviorSubject<Partial<Account>[]> = new BehaviorSubject([]);
  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  routerSub: Subscription;
  connectedCrm: string;
  showArchived = false;
  limit = 60;
  cursor: any;
  sortField = 'name';
  sortDesc = false;
  selectAllPagesForBulkUpdate = false;

  PROJECT_VISIBILITY = PROJECT_VISIBILITY;

  isLoadingAssignees = false;
  assigneeLookups: any = {};

  filterOptions = [
    {
      label: 'Tag',
      id: 'tags.id',
      icon: 'pricetag',
      type: 'select',
      values: async () => {
        this.isLoading = true;

        const tags = await this.tagsService.list({
          limit: null,
          order: ['name'],
          descending: false,
        });

        const result = tags.data.map((tag) => {
          return {
            label: tag.name,
            id: tag.id,
          };
        });

        this.isLoading = false;

        return result;
      },
    },
  ];

  select = [
    'id',
    'name',
    'imageUrl',
    'primarySupportAssigneeId',
    'primarySupportAssigneeType',
    'secondarySupportAssigneeId',
    'secondarySupportAssigneeType',
    'slackTeamIds',
    'slackChannelIds',
    'createdAt',
    'updatedAt',
    'createdBy',
    'updatedBy',
    'externalCrmMetadata',
    'externalSupportMetadata',
    'emailsAndDomains',
    'tags.name',
    'tags.id',
    'slackChannels.id',
    'slackChannels.name',
    'slackChannels.isPrivate',
    'slackTeams.id',
    'slackTeams.name',
    'externalCrmMetadata',
  ];

  constructor(
    private accountsService: AccountsService,
    public authService: AuthService,
    private modalCtrl: ModalController,
    private tagsService: TagsService,
    private analyticsService: AnalyticsService,
    private popoverCtrl: PopoverController,
    private alertCtrl: AlertController,
    private usersSrvc: UsersService,
    private userGroupsService: UserGroupsService,
    private searchablePopoverService: SearchablePopoverService,
    private msgSrvc: MessageService,
  ) {}

  async ngOnInit() {
    if (this.authService.tenant.integrations[INTEGRATIONS.salesforce.key]?.enabled) {
      this.connectedCrm = 'Salesforce';
    } else if (this.authService.tenant.integrations[INTEGRATIONS.hubspot.key]?.enabled) {
      this.connectedCrm = 'Hubspot';
    }

    this.loadAccounts();

    this.isLoadingAssignees = true;
    await Promise.all([this.loadSupportUsers(), this.loadUserGroups()]);
    this.isLoadingAssignees = false;
  }

  fetchAccounts = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<Account>>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
      select: this.select,
    };

    const where: WhereClause[] = [];

    const filterWheres = getWhereClausesFromFilters(this.filters);

    where.push(...filterWheres);

    if (this.searchText) {
      where.push({
        field: 'name',
        operator: 'like',
        value: `%${this.searchText}%`,
      });
    }

    if (where.length) {
      options.where = where;
    }

    const result = await this.accountsService.list(options);

    return result;
  };

  accountListDataSource = createDataSource(this.fetchAccounts);

  async loadAccounts() {
    this.isLoading = true;

    await this.accountListDataSource.firstPage();

    this.isLoading = false;
  }

  async reloadAccounts() {
    this.isLoading = true;

    await this.accountListDataSource.reloadPage();

    this.isLoading = false;
  }

  async loadSupportUsers() {
    const supportUsers = await this.usersSrvc.list({
      limit: null,
      order: ['name'],
      descending: false,
      where: [
        {
          field: 'name',
          operator: '!=',
          value: null,
        },
        {
          field: 'email',
          operator: '!=',
          value: null,
        },
      ],
    });
    supportUsers.data.forEach((u) => {
      this.assigneeLookups[u.id] = {
        id: u.id,
        displayName: `${u.name}${u.email ? ` (${u.email})` : ''}`,
        name: u.name,
        email: u.email,
        type: SUPPORT_STEP_ASSIGNEE_TYPES.USER,
      };
    });
  }

  async loadUserGroups() {
    const userGroups = await this.userGroupsService.list({
      limit: null,
      order: ['name'],
      descending: false,
      where: [
        {
          field: 'name',
          operator: '!=',
          value: null,
        },
      ],
    });

    userGroups.data.forEach((u) => {
      this.assigneeLookups[u.id] = {
        id: u.id,
        displayName: `${u.name} (Group)`,
        name: u.name,
        type: SUPPORT_STEP_ASSIGNEE_TYPES.TEAM,
      };
    });
  }

  showAssigneeOptions(account: Account, isPrimary: boolean) {
    this.searchablePopoverService.assignees({
      event,
      selectedId: isPrimary ? account.primarySupportAssigneeId : account.secondarySupportAssigneeId,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: true,
      callback: async (id: string, _name: string, object: any) => {
        // TODO: need to consolidate team/group/userGroup terminology
        const assigneeType =
          object?.type === SUPPORT_STEP_ASSIGNEE_TYPES.USER ? SUPPORT_STEP_ASSIGNEE_TYPES.USER : 'userGroup';

        const updates: Partial<Account> = {};

        if (isPrimary) {
          account.primarySupportAssigneeId = !id || id === account.primarySupportAssigneeId ? null : id;
          updates.primarySupportAssigneeType = assigneeType;
          updates.primarySupportAssigneeId = account.primarySupportAssigneeId;
        } else {
          account.secondarySupportAssigneeId = !id || id === account.secondarySupportAssigneeId ? null : id;
          updates.secondarySupportAssigneeType = assigneeType;
          updates.secondarySupportAssigneeId = account.secondarySupportAssigneeId;
        }

        this.updateAccount(account.id, updates);
      },
    });
  }

  onSelect({ selected }) {
    this.selectedAccounts.splice(0, this.selectedAccounts.length);
    this.selectedAccounts.push(...selected);
    this.selectAllPagesForBulkUpdate = false;
  }

  getRowClass() {
    return 'border-bottom';
  }

  openLink(url: string) {
    window.open(url, '_blank');
  }

  showNextPage() {
    this.pageCount += 1;
  }

  async manageTags() {
    const modal = await this.modalCtrl.create({
      component: EditTagLinksComponent,
      componentProps: {
        objectIds: this.selectedAccounts.map((a) => a.id),
        addCallback: async (tagId, accountIds) => {
          await this.tagsService.createAccountLinks(tagId, accountIds);
          await this.reloadAccounts();
        },
        removeCallback: async (tagId, accountIds) => {
          await this.tagsService.deleteAccountLinks(tagId, accountIds);
          await this.reloadAccounts();
        },
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.selectedAccounts = [];
    });
  }

  async showEditOptions() {
    const options = [
      {
        label: 'Edit tags',
        icon: 'pricetags-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (index: number) => {
          popover.dismiss();

          switch (index) {
            case 0: {
              this.manageTags();

              break;
            }
            default:
              break;
          }
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  async addIndividualAccount() {
    const modal = await this.modalCtrl.create({
      component: EditAccountComponent,
      cssClass: 'modal-medium',
    });

    await modal.present();

    modal.onDidDismiss().then(() => {
      this.loadAccounts();
    });
  }

  handleFilters() {
    this.loadAccounts();
  }

  async updateAccount(accountId: string, updates: Partial<Account>) {
    try {
      await this.accountsService.updatePartial(accountId, updates);

      this.msgSrvc.show('✔︎ Updated');
    } catch (err) {
      console.log(err);

      this.msgSrvc.show('Something went wrong');
    }
  }

  getTagsForRow(row: any) {
    return row.tags?.map((tag) => tag.name).join(', ');
  }

  async editTagForAccount(accountId: string, tagId: string) {
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options: [
          {
            label: 'Remove tag',
            icon: 'close-circle',
          },
        ],
        callback: async (index: number, _label: string) => {
          popover.dismiss();

          if (index === 0) {
            await this.tagsService.deleteAccountLinks(tagId, [accountId]);
            this.loadAccounts();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  addTagToAccount(accountId: string) {
    this.searchablePopoverService.tags({
      event,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Account List', 'Add Tag From Row');

        await this.tagsService.createAccountLinks(id, [accountId]);

        this.reloadAccounts();
      },
    });
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.loadAccounts();
  }

  async linkCrm(row: Account) {
    this.searchablePopoverService.externalCrmAccounts({
      selectedAccountId: row.externalCrmMetadata?.id,
      event,
      initialSearchText: row.name,
      callback: async (id: string, name: string) => {
        const alert = await this.alertCtrl.create({
          header: 'Confirm account linking',
          subHeader: `This will link the ${name} profile to the ${row.name} account`,
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Confirm',
              handler: async () => {
                await this.accountsService.updatePartial(row.id, {
                  externalCrmMetadata: { id },
                });
                this.reloadAccounts();
              },
            },
          ],
        });

        await alert.present();
      },
    });
  }

  async unlinkCrm(row: Account) {
    await this.accountsService.update(row.id, {
      externalCrmMetadata: { id: null },
    });

    this.reloadAccounts();
  }

  async exportAsCsv() {
    this.analyticsService.trackEvent('Account List', 'Export CSV');

    const date = new Date();

    // Get unpaginated list
    const accounts = await this.fetchAccounts({
      limit: null,
    });

    const results = accounts.data.map((a) => {
      return {
        ...a,
        primarySupportAssigneeName: a.primarySupportAssigneeId
          ? this.assigneeLookups[a.primarySupportAssigneeId]?.name
          : '',
        primarySupportAssigneeType: a.primarySupportAssigneeId ? a.primarySupportAssigneeType ?? 'user' : '',
        secondarySupportAssigneeName: a.secondarySupportAssigneeId
          ? this.assigneeLookups[a.secondarySupportAssigneeId]?.name
          : '',
        secondarySupportAssigneeType: a.secondarySupportAssigneeId ? a.secondarySupportAssigneeType ?? 'user' : '',
        slackTeamIds: a.slackTeamIds?.join(','),
        slackChannels: a.slackChannelIds?.join(','),
      };
    });

    const headers = [
      'name',
      'primarySupportAssigneeId',
      'primarySupportAssigneeName',
      'primarySupportAssigneeType',
      'secondarySupportAssigneeId',
      'secondarySupportAssigneeName',
      'secondarySupportAssigneeType',
      'slackTeamIds',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
      'slackChannelIds',
      'externalCrmMetadata',
      'emailsAndDomains',
      'id',
      'tags',
    ];

    downloadCSV(results, headers, `account-export-${date.getTime()}`);
  }

  async editAccount(accountId: string) {
    const modal = await this.modalCtrl.create({
      component: EditAccountComponent,
      componentProps: {
        id: accountId,
      },
      cssClass: 'modal-medium',
    });

    await modal.present();

    modal.onDidDismiss().then(() => {
      this.loadAccounts();
    });
  }
}
